import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import s from './s.module.less';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { EPPAStep, IPPAPersonalIdentification } from 'types/ppaOnboarding';
import { FormIdentify } from 'types/form';

// Wrapper component to make FileUploadPrivate work with Form.Item
interface FileUploadWrapperProps {
    value?: string;
    onChange?: (value: string) => void;
    uploadType: 'PROSPECT_INSURANCE' | 'PROSPECT_IDENTITY' | 'IMAGE' | 'LICENSE';
    title: string;
    tips: string;
    accept: string;
}

const FileUploadWrapper: React.FC<FileUploadWrapperProps> = ({ value, onChange, uploadType, title, tips, accept }) => {
    return (
        <div className={s.uploadItem}>
            <FileUploadPrivate
                value={value}
                onChange={onChange}
                uploadType={uploadType}
                title={title}
                tips={tips}
                accept={accept}
                previewMode="vertical"
            />
        </div>
    );
};

interface IProps {
    formId: string;
    onNext: (data: IPPAPersonalIdentification, step: EPPAStep) => void;
    onSaveStep: () => void;
    setNextButtonDisabled: (disabled: boolean) => void;
    initValue?: FormIdentify | null;
}

const Step3: React.FC<IProps> = ({ formId, onNext, setNextButtonDisabled, initValue }) => {
    const [form] = Form.useForm();

    const [idTypeOptions] = useGetSettingData(EnumFields.IDENTITY_TYPE);
    const idTypeVal = Form.useWatch('idType', form);
    const frontIdFileVal = Form.useWatch('frontIdFile', form);
    const backIdFileVal = Form.useWatch('backIdFile', form);

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue({
                idType: initValue.type,
                frontIdFile: initValue.frontUrl,
                backIdFile: initValue.backUrl,
            });
        }
    }, [initValue, form]);

    useEffect(() => {
        // ID type is required; keep Next disabled until the type and its required files are provided.
        if (!idTypeVal) {
            setNextButtonDisabled(true);
        } else if (idTypeVal !== 'passport' && !backIdFileVal) {
            setNextButtonDisabled(true);
        } else if (!frontIdFileVal) {
            setNextButtonDisabled(true);
        } else {
            setNextButtonDisabled(false);
        }
    }, [setNextButtonDisabled, idTypeVal, frontIdFileVal, backIdFileVal]);

    const onFinish = async (values: { idType?: string; frontIdFile?: string; backIdFile?: string }) => {
        try {
            // ID type is required: validate the form so empty fields block submission.
            await form.validateFields();

            // 验证通过后，提交数据
            onNext(
                {
                    identification: {
                        type: values.idType || '',
                        frontUrl: values.frontIdFile || '',
                        backUrl: values.backIdFile || '',
                    },
                },
                EPPAStep.STEP_11,
            );
        } catch (error) {
            // 验证失败时，不提交数据
            // Ant Design Form会自动显示错误信息
        }
    };

    return (
        <div className={s.wrap}>
            <div className={s.content}>
                {/* Header Section */}
                <div className={s.header}>
                    <h1>Upload your government-issued ID</h1>
                    <div className={s.headerDescription}>
                        We use your ID to verify your identity. It will never be shown to patients.
                    </div>
                </div>

                {/* Form Section */}
                <Form id={formId} form={form} layout="vertical" onFinish={onFinish} className={s.formSection}>
                    {/* ID Type Selection */}
                    <Form.Item
                        name="idType"
                        label="ID type"
                        className={s.formItem}
                        rules={[{ required: true, message: 'Please select your ID type' }]}
                    >
                        <Select
                            className={s.selectDropdown}
                            placeholder="Select your personal identification"
                            popupClassName={s.selectDropdownPopup}
                            fieldNames={{ label: 'content', value: 'dictKey' }}
                            options={idTypeOptions}
                        />
                    </Form.Item>

                    {/* ID Upload Section - Show when ID type is selected */}
                    <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.idType !== currentValues.idType}>
                        {({ getFieldValue }) => {
                            const idType = getFieldValue('idType');
                            return idType ? (
                                <div className={s.uploadSection}>
                                    <label className={s.uploadLabel}>Upload your ID</label>
                                    <div className={s.uploadContainer}>
                                        {/* Front ID Upload */}
                                        <Form.Item
                                            name="frontIdFile"
                                            rules={
                                                idTypeVal
                                                    ? [
                                                          {
                                                              required: true,
                                                              message: 'Please upload the front of your ID',
                                                          },
                                                      ]
                                                    : []
                                            }
                                            required={false}
                                            className={s.uploadFormItem}
                                            validateTrigger={['onChange', 'onBlur']}
                                        >
                                            <FileUploadWrapper
                                                uploadType="PROSPECT_IDENTITY"
                                                title="Front of your ID"
                                                tips="PNG, JPG, GIF up to 10MB"
                                                accept="image/*"
                                            />
                                        </Form.Item>

                                        {/* Back ID Upload */}
                                        {idType !== 'passport' && (
                                            <Form.Item
                                                name="backIdFile"
                                                rules={
                                                    idType
                                                        ? [
                                                              {
                                                                  required: true,
                                                                  message: 'Please upload the back of your ID',
                                                              },
                                                          ]
                                                        : []
                                                }
                                                required={false}
                                                className={s.uploadFormItem}
                                                validateTrigger={['onChange', 'onBlur']}
                                            >
                                                <FileUploadWrapper
                                                    uploadType="PROSPECT_IDENTITY"
                                                    title="Back of your ID"
                                                    tips="PNG, JPG, GIF up to 10MB"
                                                    accept="image/*"
                                                />
                                            </Form.Item>
                                        )}
                                    </div>
                                </div>
                            ) : null;
                        }}
                    </Form.Item>

                    {/* Optional Submit Button for debugging */}
                    {/* <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Submit ID Information
                        </Button>
                    </Form.Item> */}
                </Form>
            </div>
        </div>
    );
};

export default Step3;
